import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'orange';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
    table: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }

  interface Palette {
    orange: PaletteColor;
  }
  interface PaletteOptions {
    orange?: SimplePaletteColorOptions;
  }

  interface CommonColors {
    customBlue: string;
  }
}

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  1000: '#18212E',
  1100: '#0D1016',
  1200: '#080A0F',
  1300: '#040507',
};

const PRIMARY = {
  lighter: '#ffe6ed',
  light: '#ffbfd2',
  main: '#fe344f',
  dark: '#B7195F',
  darker: '#7A0951',
  contrastText: '#FFFFFF',
};

const SECONDARY = {
  lighter: '#D4FBF6',
  light: '#7CE8EB',
  main: '#36aac1',
  dark: '#146288',
  darker: '#07325B',
  contrastText: '#FFFFFF',
};

const INFO = {
  lighter: '#D2F4FE',
  light: '#79D0FB',
  main: '#2196F3',
  dark: '#1057AE',
  darker: '#062B74',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#E7FBDE',
  light: '#A3E798',
  main: '#4CAF50',
  dark: '#267D39',
  darker: '#0E532A',
  contrastText: '#ffffff',
};

const WARNING = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#FFEBEE',
  light: '#FFCDD2',
  main: '#F44336',
  dark: '#D32F2F',
  darker: '#B71C1C',
  contrastText: '#FFFFFF',
};

const ORANGE = {
  lighter: '#fff3e0',
  light: '#ffb74d',
  main: '#ff9800',
  dark: '#f57c00',
  darker: '#e65100',
  contrastText: '#FFFFFF',
};

const COMMON = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
    customBlue: '#133861',
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  orange: ORANGE,
  grey: GREY,
  divider: alpha(GREY[400], 0.2),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export function palette(mode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: {
      paper: '#FFFFFF',
      table: alpha(GREY[400], 0.12),
      default: '#FFFFFF',
      neutral: GREY[200],
    },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  };

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      table: alpha(GREY[600], 0.12),
      default: GREY[900],
      neutral: alpha(GREY[500], 0.12),
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  };

  return mode === 'light' ? light : dark;
}
